import React, {useCallback, useRef, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import PeriodInput from "../../../components/input/period-input.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import InputCheckbox from "../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaFileExcel, FaPlus, FaQuestion, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";
import projectUtils from "../../../utils/project-utils.js";
import AttendanceService from "../../../services/dispatch/attendance-service.js";
import DispatchListOptionsService from "../../../services/dispatch/dispatch-list-options-service.js";
import {
    exportGridData,
    sanitizeFilterValue,
    selectedValue,
    selectedValues,
    validateDateInterval,
} from "../utils/dispatch-list-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialFilters = {
    dtStart: "",
    dtEnd: "",
    protocol: "",
    userNameWar: "",
    schedule: false,
    channel: null,
    reason: [],
    address: "",
    containedText: "",
};

const buildFilter = (values) => [
    {field: "users.namewar", type: "like", value: sanitizeFilterValue(values.userNameWar)},
    {field: "attendance_start", type: "keywords", value: {dtStart: values.dtStart || "", dtEnd: values.dtEnd || ""}},
    {field: "attendance_protocol", type: "like", value: sanitizeFilterValue(values.protocol)},
    {field: "attendance_channel", type: "like", value: sanitizeFilterValue(selectedValue(values.channel))},
    {field: "schedule", type: "like", value: values.schedule},
    {field: "address", type: "like", value: sanitizeFilterValue(values.address)},
    {field: "containedText", type: "like", value: sanitizeFilterValue(values.containedText)},
    {field: "reason", type: "in", value: selectedValues(values.reason)},
];

const exportRow = (row) => ({
    "ID": row.attendance_id,
    "Protocolo": row.attendance_protocol,
    "Data/Hora inicial": row.attendance_start,
    "Data/Hora final": row.attendance_end,
    "Natureza": row.attendance_nature,
    "Canal": row.attendance_channel,
    "Telefone": row.attendance_contactphone,
    "Solicitante": row.attendance_contactname,
    "Endereço do fato": row.attendance_factaddress,
    "Prioridade": row.attendance_priority,
    "Status": row.attendance_statusname,
    "Última alteração": row.attendance_lastuser,
});

const HIDDEN_COLUMNS_STORAGE = "attendance-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["attendance_id", "attendance_contactphone", "attendance_contactname", "attendance_lastuser"];

const baseColumns = [
    {field: "attendance_id", headerName: "ID", sort: "desc", width: 90, cellDataType: "number"},
    {field: "attendance_protocol", headerName: "Protocolo", width: 130, comparator: aggridUtils.agGridNumberYearComparator},
    {field: "attendance_start", headerName: "Data/Hora inicial", width: 160, comparator: aggridUtils.agGridCompareDateTime},
    {field: "attendance_end", headerName: "Data/Hora final", width: 160, comparator: aggridUtils.agGridCompareDateTime},
    {field: "attendance_nature", headerName: "Natureza", minWidth: 220, flex: 1},
    {field: "attendance_channel", headerName: "Canal", minWidth: 130},
    {field: "attendance_contactphone", headerName: "Telefone", minWidth: 130},
    {field: "attendance_contactname", headerName: "Solicitante", minWidth: 150},
    {field: "attendance_factaddress", headerName: "Endereço do fato", minWidth: 360, flex: 1},
    {field: "attendance_priority", headerName: "Prioridade", minWidth: 120},
    {field: "attendance_statusname", headerName: "Status", minWidth: 130},
    {field: "attendance_lastuser", headerName: "Última alteração", minWidth: 160},
];

const buildColumnDefs = () => {
    const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
    return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
};

const AttendanceList = () => {
    const navigate = navigateHook();
    const hasPermission = useHasPermission();
    const gridRef = useRef(null);
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const canManageAttendance = hasPermission(PermissionEnum.ATTENDANCE_WEB);

    const {data = [], isLoading} = useQuery({
        queryFn: () => AttendanceService.list({filter: [...filter]}),
        queryKey: ["dispatch-attendance", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: channelOptions = [], isLoading: isLoadingChannels} = useQuery({
        queryFn: () => DispatchListOptionsService.getAttendanceChannelFullList(),
        queryKey: ["dispatch-attendance-channels"],
    });

    const {data: natureOptions = [], isLoading: isLoadingNatures} = useQuery({
        queryFn: () => DispatchListOptionsService.dispatchNatures(),
        queryKey: ["dispatch-natures"],
    });

    const [columnDefs, setColumnDefs] = useState(buildColumnDefs);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => {
        const message = validateDateInterval(filterValues.dtStart, filterValues.dtEnd);
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const openForm = () => navigate(API_DESPACHO_WEB + "/attendance/form", false);

    const onRowClicked = ({data: row}) => {
        if (!row?.attendance_id) return;

        if (canManageAttendance) {
            navigate(`${API_DESPACHO_WEB}/attendance/form/${row.attendance_id}`, true,);
            return;
        }

        navigate(
            `/reports/attendance_info?id=${row.attendance_id}`,
            true,
        );
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading || isLoadingChannels || isLoadingNatures}>
            <Template.Header>
                <Template.Title>Atendimentos</Template.Title>
                <div className={"flex gap-2"}>
                    <Button target="success" onClick={() => exportGridData(gridRef.current?.api, exportRow)} title="Exportar">
                        <FaFileExcel/> Exportar
                    </Button>
                    {canManageAttendance &&
                        <Button onClick={openForm}><FaPlus/>Adicionar atendimento</Button>
                    }
                </div>
            </Template.Header>
            <Template.Body className="flex flex-col h-full min-h-0 space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <PeriodInput
                                startDateId="dtStart"
                                endDateId="dtEnd"
                                label="Início entre"
                                dateFormat="LL"
                                returnDateObj={false}
                                startDate={filterValues.dtStart}
                                endDate={filterValues.dtEnd}
                                setFilter={setFilterValues}
                                cols={6}
                            />
                            <InputForm classNameWrapper="col-span-2" id="protocol" label="Protocolo" value={filterValues.protocol} onChange={handleInput}/>
                            <InputForm classNameWrapper="col-span-2" id="userNameWar" label="Cadastrado por" value={filterValues.userNameWar} onChange={handleInput}/>
                            <div className="col-span-2 flex h-[32px] items-center">
                                <InputCheckbox
                                    id="schedule"
                                    label="Cadastrado por agendamento"
                                    checked={filterValues.schedule}
                                    onChange={({target}) => setFilterValues(prev => ({...prev, schedule: target.checked}))}
                                />
                            </div>

                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="channel"
                                label="Canal"
                                options={channelOptions}
                                value={filterValues.channel}
                                onChange={(option) => setFilterValues(prev => ({...prev, channel: option}))}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-5"
                                id="reason"
                                label="Natureza"
                                isMulti
                                wrapValues
                                options={natureOptions}
                                value={filterValues.reason}
                                onChange={(options) => setFilterValues(prev => ({...prev, reason: options ?? []}))}
                            />
                            <InputForm classNameWrapper="col-span-4" id="address" label="Endereço" value={filterValues.address} onChange={handleInput}/>

                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id="containedText"
                                    label="Texto contido"
                                    inputClass="pr-9"
                                    title="Filtros: Nome do solicitante, observação do atendente, observação final e transcrição"
                                    value={filterValues.containedText}
                                    onChange={handleInput}
                                    span={
                                        <span
                                            className="absolute right-2 top-1/2 z-20 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#E9ECEF] text-[#676a6c]"
                                            title="Filtros: Nome do solicitante, observação do atendente, observação final e transcrição"
                                            aria-label="Campos pesquisados pelo filtro Texto contido"
                                        >
                                            <FaQuestion size={10}/>
                                        </span>
                                    }
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-241px)] aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AttendanceList;
